import clsx from "clsx";
import { FC, useEffect, useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { MsgExec, priceFormatter } from "rujira.js";
import {
  Decimal,
  /* Decimal, */
  i18n,
  Input,
  Slider,
} from "rujira.ui";
import { Info } from "rujira.ui/src/components/icons/Icons";
import { usePreloadedBalance } from "../../common/components/Balance";
import { useNotificationModal } from "../../common/components/Notifications";
import {
  MsgProvider,
  TxBalanceAccountButton,
} from "../../common/components/TxButton";
import {} from "../../portfolio/utils";
import { Side } from "../types";
import {
  SubmitOracleFragment$data,
  SubmitOracleFragment$key,
} from "./__generated__/SubmitOracleFragment.graphql";
import { AutoClaimerToggle } from "./AutoClaimerToggle";
import { useAmount } from "./Context";
import { InputQuantity } from "./InputQuantity";

const fragment = graphql`
  fragment SubmitOracleFragment on FinPair {
    address
    tick
    assetBase {
      asset
      type
      chain
      metadata {
        decimals
        symbol
      }
    }
    assetQuote {
      asset
      type
      chain
      metadata {
        decimals
        symbol
      }
    }
    oracleBase {
      id
      price
    }
    oracleQuote {
      id
      price
    }
    book {
      bids {
        price
      }
      center
      asks {
        price
      }
    }
    feeMaker
    feeTaker
  }
`;

interface Props {
  k: SubmitOracleFragment$key;
  side: Side;
}

export const SubmitOracle: FC<
  Props & { oracleBase: bigint; oracleQuote: bigint }
> = ({ k, side, oracleBase, oracleQuote }) => {
  const [amount, setAmount] = useAmount();
  const d = useFragment(fragment, k);
  const [value_, setValue] = useState(side === Side.Quote ? "30" : "-30");
  const value = Number(value_);
  useEffect(() => {
    setValue(side === Side.Quote ? "30" : "-30");
  }, [side]);

  const { request } = useNotificationModal();
  const { account } = usePreloadedBalance();

  const msg = useMemo(() => {
    if (!account) return null;
    return new MsgExec(
      { address: account.address, network: "THOR" },
      account.asset,
      amount,
      d.address,
      {
        order: [[[side, { oracle: value }, amount.toString()]], null],
      }
    );
  }, [side, amount, d.address, value]);

  const price = (oracleBase * 1000000000000n) / oracleQuote;
  const rate = adjust(price, value);

  const returned =
    side === Side.Base
      ? (amount * rate) / 10n ** 12n
      : (10n ** 12n * amount) / rate;

  const fee = (returned * (d.feeTaker || 0n)) / 10n ** 12n;

  return (
    <MsgProvider msg={msg}>
      <InputQuantity amount={amount} setAmount={setAmount}>
        <i18n.label>
          Qty.{" "}
          <small>
            (
            {side === Side.Quote
              ? d.assetQuote.metadata.symbol
              : d.assetBase.metadata.symbol}
            )
          </small>
        </i18n.label>
      </InputQuantity>
      <div className="row wrap pad-mini">
        <div className="col w-14">
          <Input
            label="Deviation (bps)"
            className="numeric-input numeric-input--white condensed"
            type="number"
            min="-10000"
            max="10000"
            value={value_}
            onChange={(e) => {
              setValue(e.currentTarget.value);
            }}
          />
        </div>

        <div className="col grow">
          <table className="table table--xs condensed fs-12 color-grey">
            <tbody>
              <tr>
                <td
                  data-tooltip-id="global-tip"
                  data-tooltip-html={`<div class="w-25">The oracle price is based on the spot price calculated across THORChain Base Layer pools.</div>`}>
                  <div className="flex ai-c">
                    Oracle{" "}
                    <span className="color-white mx-0.5">
                      {d.assetBase.metadata.symbol}
                    </span>{" "}
                    price:
                    <Info
                      className="h-a ml-0.5 block"
                      style={{ width: "0.875rem" }}
                    />
                  </div>
                </td>
                <td className="text-right">
                  <span className="color-white mono fs-11">
                    {priceFormatter(price)}
                  </span>{" "}
                  {d.assetQuote.metadata.symbol}
                </td>
              </tr>
              <tr
                className={clsx({
                  "color-red":
                    (value > 100 && side === Side.Quote) ||
                    (value < -100 && side === Side.Base),
                  "color-teal":
                    (value > 0 && side === Side.Base) ||
                    (value < 0 && side === Side.Quote),
                })}>
                <td>
                  <span>{value > 0 ? "Premium" : "Discount"}:</span>
                </td>
                <td
                  className={clsx({
                    "text-right mono fs-11": true,
                    "color-white":
                      (value > 0 && value <= 100 && side === Side.Quote) ||
                      (value < 0 && value >= -100 && side === Side.Base),
                  })}>
                  {value >= 0 ? (
                    <span>{(value / 100).toLocaleDecimal(2)}%</span>
                  ) : (
                    <span>{(-value / 100).toLocaleDecimal(2)}%</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Current execution price:</td>
                <td className="text-right">
                  <span className="color-white fs-11 mono">
                    {priceFormatter(rate)}
                  </span>{" "}
                  {d.assetQuote.metadata.symbol}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Slider
        min={-250}
        max={250}
        step={1}
        value={value}
        onChange={(v) => {
          setValue(v.toString());
        }}
      />

      <AutoClaimerToggle>
        <Quote
          amount={amount}
          returned={returned}
          side={side}
          data={d || undefined}
          fee={fee}
        />
        <p
          data-tooltip-html={`
          <div class="w-25">
            ${i18n.t(
              "Buy or sell a given quantity of token at a set discount or premium to an oracle price. The order price will update every time the oracle price changes. "
            )}
          </div>
        `}
          data-tooltip-id="global-tip"
          className="color-grey fs-12 text-center flex ai-c jc-c mt-a fw-500">
          {i18n.t("What are tracking orders?")}
          <Info className="w-2 h-2 ml-0.5" />
        </p>

        <TxBalanceAccountButton
          hideSimulation
          onSuccess={request}
          required={{
            asset: side === Side.Base ? d.assetBase : d.assetQuote,
            amount,
          }}
          label={`${side === Side.Quote ? "Buy" : "Sell"} ${d.assetBase ? d.assetBase.metadata.symbol : ""}`}
          className={clsx({
            "w-full": true,
            "button--blue": side == Side.Quote,
            "button--red": side == Side.Base,
          })}
        />
      </AutoClaimerToggle>
    </MsgProvider>
  );
};

const Quote: FC<{
  data?: SubmitOracleFragment$data;
  amount: bigint;
  side: Side;
  fee: bigint;
  returned: bigint;
}> = ({ data, side, fee, returned }) => {
  return (
    <div className="flex dir-c gap-y-1 mt-1 mb-2 fs-12 color-grey condensed">
      <div className="flex ai-c grow trade__quote trade__quote--return">
        <i18n.div className="condensed color-primary1 grow fs-14">
          Estimated Return
        </i18n.div>
        <Decimal
          amount={BigInt(returned || "")}
          className="ml-a color-white fs-14"
          symbol={
            side === Side.Quote
              ? data?.assetBase.metadata.symbol
              : data?.assetQuote.metadata.symbol
          }
          symbolClassName="color-grey condensed symbol inline-block text-left"
        />
      </div>
      <div
        className="flex"
        data-tooltip-id="global-tip"
        data-tooltip-float={true}
        data-tooltip-content="0.075% Maker Fee paid in output token when filled orders are claimed">
        <i18n.div className="col">Estimated Fee*</i18n.div>
        <div className="col text-right color-white">
          {priceFormatter(fee * 10000n)}
          <span className="color-grey condensed symbol inline-block text-left ml-0.5">
            {side === Side.Quote
              ? data?.assetBase.metadata.symbol
              : data?.assetQuote.metadata.symbol}
          </span>
        </div>
      </div>
    </div>
  );
};

const adjust = (price: bigint, bps: number): bigint => {
  if (!price) return 0n;
  return (price * (10000n + BigInt(bps))) / 10000n;
};
